// React & React Hooks
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

// Third-party libraries
import {
  Box,
  Card,
  Grid,
  Paper,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Button,
  Badge,
} from "@mui/material";
import { map } from "lodash";

// MUI Icons
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

// Context / Providers
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";

// Services / utilities
import { httpGet, httpPost } from "../../services/commonService";

// Project constants
import urlConstant from "../../constants/urlConstants";
import dataConstant from "../../constants/dataConstants";
import { messageConstant } from "../../constants/messageConstant";

// Shared / reusable components
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";
import SnackState from "../../shared-components/snackbar/snackState";
import PaymentPopup from "./PaymentPopup";

// Initialize date variables
const initialOrderDate = new Date();
const initialOrderEntryDate = new Date();

const DataCard = ({ title, icon, children }) => {
  return (
    <Card
      sx={(theme) => ({
        background: theme.palette.secondary.lightest,
        borderRadius: "20px",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        padding: "20px",
      })}
    >
      <Box display="flex" width="100%" gap={2} mb={"25px"} alignItems="center">
        {icon}
        <Typography color="secondary.light" variant="h5" fontWeight={500}>
          {title}
        </Typography>
      </Box>
      {children}
    </Card>
  );
};

const formatCurrency = (value) => {
  return "$" + (value || 0).toFixed(dataConstant.TwoDigitConstValue);
};

const ViewOrder = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const authenticationState = useContext(AuthenticationContext);
  const [snack, closeSnack, showSnackbar] = SnackState();

  const [paymentCards, setPaymentCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(0);
  const [isPaymentPopupOpen, setIsPaymentPopupOpen] = useState(false);
  const [loading, setloading] = useState(false);

  const [state, setState] = useState({
    orderDet: {
      accountID: 0,
      invoiceId: 0,
      orderDate: initialOrderDate,
      orderEntryDate: initialOrderEntryDate,
      orderStatus: "",
      firstName: "",
      lastName: "",
      billingFirstName: "",
      billingLastName: "",
      billingPhone: "",
      billingMobile: "",
      billingAddress1: "",
      billingAddress2: "",
      billingCity: "",
      billingState: "",
      billingCountry: "",
      billingZipcode: "",
      shippingFirstName: "",
      shippingLastName: "",
      shippingPhone: "",
      shippingMobile: "",
      shippingAddress1: "",
      shippingAddress2: "",
      shippingCity: "",
      shippingState: "",
      shippingCountry: "",
      shippingZipcode: "",
      checkBoxValue: false,
      orderItem: [],
      orderPayment: [],
      status: "",
      shippingMethod: "",
      TrackingNumber: "",
    },
    orderSummryDetails: {
      cartTotal: 0,
      discount: 0,
      subTotal: 0,
      shipping: 0,
      tax: 0,
      total: 0,
      subTotalDiscount: 0,
      orderTotalDiscount: 0,
    },
  });

  useEffect(() => {
    if (id) {
      const orderId = id ? parseInt(id, 10) : 0;
      const getOrderURL = `${urlConstant.Order.OrderDetails}?Id=${orderId}`;
      httpGet(
        getOrderURL,
        true,
        true,
        authenticationState.accessToken,
        authenticationState.impersonateEmail
      )
        .then((response) => {
          const responseData = response.Value.Data;
          if (response.Value.Status === dataConstant.APIResponseStatus.Failure) {
            showSnackbar(response.Value.Message, "error");
            setTimeout(() => {
              navigate("/order");
            }, 2000);
          } else {
            const orderDet = {
              firstName: responseData.FirstName,
              orderDate: new Date(responseData.OrderDate),
              orderEntryDate: new Date(responseData.OrderEntryDate),
              accountID: responseData.AccountID,
              invoiceId: responseData.InvoiceNo,
              orderStatus: responseData.OrderStatus,
              billingPhone: responseData.BillingPhone,
              lastName: responseData.LastName,
              billingMobile: responseData.BillingMobile,
              billingFirstName: responseData.BillingFirstName,
              billingLastName: responseData.BillingLastName,
              billingAddress1: responseData.BillingAddress1,
              billingAddress2: responseData.BillingAddress2,
              billingCity: responseData.BillingCity,
              billingCountry: responseData.BillingCountry,
              billingState: responseData.BillingState,
              billingZipcode: responseData.BillingZipcode,
              shippingMobile: responseData.ShippingMobile,
              shippingPhone: responseData.ShippingPhone,
              shippingFirstName: responseData.ShippingFirstName,
              shippingLastName: responseData.ShippingLastName,
              shippingAddress1: responseData.ShippingAddress1,
              shippingAddress2: responseData.ShippingAddress2,
              shippingCity: responseData.ShippingCity,
              shippingCountry: responseData.ShippingCountry,
              shippingState: responseData.ShippingState,
              shippingZipcode: responseData.ShippingZipcode,
              orderItem: responseData.ItemdetailList,
              orderPayment: responseData.PaymentMethoddetailList,
              status: responseData.OrderStatus,
              shippingMethod: responseData.ShippingMethod,
              TrackingNumber: responseData.TrackingNumber,
            };
            const orderSummryDetails = {
              cartTotal: responseData.CartTotalAmount,
              discount: responseData.DiscountAmount,
              subTotal: responseData.SubTotal,
              shipping: responseData.DiscountedShippingAmount,
              tax: responseData.TaxAmount,
              total: responseData.TotalAmount,
              subTotalDiscount: responseData.SubTotalDiscount,
              orderTotalDiscount: responseData.OrderTotalDiscount,
            };
            setState((prevState) => ({
              ...prevState,
              orderDet,
              orderSummryDetails,
            }));
            setloading(true);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }, [id]);

  const handlePaymentPopupOpen = () => {
    const getExistCardListURL = `${urlConstant.Order.ExistingCardList}`;

    httpPost(
      getExistCardListURL,
      {},
      true,
      true,
      authenticationState.accessToken,
      authenticationState.impersonateEmail
    )
      .then((response) => {
        const responseData = response.Value.Data || [];
        setPaymentCards(responseData);
        setSelectedCard(responseData.length > 0 ? responseData[0].value : 0);
        setIsPaymentPopupOpen(true);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleCardSelection = (event) => {
    setSelectedCard(parseInt(event.target.value, 10));
  };

  const handlePaymentSubmit = () => {
    const modelData = {
      OrderId: parseInt(id, 10),
      PaymentProfileId: selectedCard,
    };

    const orderRepayment = `${urlConstant.Order.OrderRepayment}`;

    httpPost(
      orderRepayment,
      modelData,
      true,
      true,
      authenticationState.accessToken,
      authenticationState.impersonateEmail
    )
      .then((response) => {
        if (response?.Status === dataConstant.APIResponseStatus.Success) {
          showSnackbar(response.description, "success");
          handlePaymentPopupClose();
          setTimeout(() => {
            navigate("/order");
          }, 1000);
          return;
        }
        showSnackbar(response?.error, "error");
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleNavigateToPaymentProfile = () => {
    navigate("/membership");
  };

  const handlePaymentPopupClose = () => {
    setIsPaymentPopupOpen(false);
  };

  const handleNavigateToOrderList = () => {
    navigate("/order");
  };

  return (
    <>
      {loading ? (
        <Box flexDirection="column" gap={2} display="flex">
          <Typography color="secondary.light" variant="h4" fontWeight={600}>
            {t("My Account")}
          </Typography>
          <Box width="100%">
            <Card
              sx={(theme) => ({
                p: { xs: "20px", sm: "25px 30px" },
                background: theme.palette.background.cardGradient,
                border: "1px solid var(--headerbg)",
                borderRadius: "20px",
                height: "100%",
              })}
            >
              <Stack gap="20px">
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  gap={"20px"}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  flexWrap="wrap"
                >
                  <Typography
                    color="secondary.light"
                    variant="h4"
                    fontWeight={600}
                  >
                    {t("Order.Order History Details")}
                  </Typography>
                  <Box className="label-data">
                    <Stack
                      direction="row"
                      alignItems="center"
                      flexWrap={"wrap"}
                      gap={"20px"}
                    >
                      <Stack direction="row" alignItems="center" gap={"10px"}>
                        <DescriptionOutlinedIcon
                          color="secondary"
                          sx={{ color: "var(--secondary-text)" }}
                        />
                        <Typography variant="body1" color="secondary.text">
                          {t("Order.INVOICE#")} ({state.orderDet.invoiceId})
                        </Typography>
                      </Stack>
                      {state.orderDet.orderStatus === dataConstant.Pending && (
                        <Button
                          sx={{ px: 3 }}
                          variant="contained"
                          onClick={handlePaymentPopupOpen}
                        >
                          {t("Order.Pay")}
                        </Button>
                      )}
                    </Stack>
                  </Box>
                </Stack>
                <Grid container spacing={2}>
                  {/* Shipping Address */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <DataCard
                      title={t("Order.Shipping Address")}
                      icon={
                        <LocationOnOutlinedIcon
                          sx={{
                            color: "var(--secondary-text)",
                            width: "1.2em",
                            height: "1.2em",
                          }}
                        />
                      }
                    >
                      <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                          background: "transparent",
                          width: "100%",
                          "& .MuiTableRow-root:last-child .MuiTableCell-root": {
                            pb: 1,
                          },
                        }}
                        className="two-table"
                      >
                        <Table aria-label="simple table">
                          <TableBody>
                            <TableRow>
                              <TableCell className="label-data">
                                {t("Order.NAME")}
                              </TableCell>
                              <TableCell align="right">
                                {state.orderDet.shippingFirstName}{" "}
                                {state.orderDet.shippingLastName}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className="label-data">
                                {t("Order.CITY")}
                              </TableCell>
                              <TableCell align="right">
                                {state.orderDet.shippingCity}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </DataCard>
                  </Grid>

                  {/* Billing Address */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <DataCard
                      title={t("Order.Billing Address")}
                      icon={
                        <LocationOnOutlinedIcon
                          sx={{
                            color: "var(--secondary-text)",
                            width: "1.2em",
                            height: "1.2em",
                          }}
                        />
                      }
                    >
                      <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                          background: "transparent",
                          width: "100%",
                          "& .MuiTableRow-root:last-child .MuiTableCell-root": {
                            pb: 1,
                          },
                        }}
                        className="two-table"
                      >
                        <Table aria-label="simple table">
                          <TableBody>
                            <TableRow>
                              <TableCell className="label-data">
                                {t("Order.NAME")}
                              </TableCell>
                              <TableCell align="right">
                                {state.orderDet.billingFirstName}{" "}
                                {state.orderDet.billingLastName}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className="label-data">
                                {t("Order.CITY")}
                              </TableCell>
                              <TableCell align="right">
                                {state.orderDet.billingCity}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </DataCard>
                  </Grid>

                  {/* Shipping Details */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <DataCard
                      title={t("Order.Shipping Details")}
                      icon={
                        <img
                          src="/icons/Cube.svg"
                          alt="shipping"
                          width="25"
                          height="25"
                        />
                      }
                    >
                      <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                          background: "transparent",
                          width: "100%",
                          "& .MuiTableRow-root:last-child .MuiTableCell-root": {
                            pb: 1,
                          },
                        }}
                        className="two-table"
                      >
                        <Table aria-label="simple table">
                          <TableBody>
                            <TableRow>
                              <TableCell className="label-data">
                                {t("Order.STATUS")}
                              </TableCell>
                              <TableCell align="right">
                                {state.orderDet.TrackingNumber
                                  ? state.orderDet.TrackingNumber
                                  : messageConstant.Order
                                      .NoTrackingDetailsAvailable}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </DataCard>
                  </Grid>

                  {/* Payment Method */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <DataCard
                      title={t("Order.Payment Method")}
                      icon={
                        <img
                          src="/icons/Money.svg"
                          alt="shipping"
                          width="25"
                          height="25"
                        />
                      }
                    >
                      <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                          background: "transparent",
                          width: "100%",
                          "& .MuiTableRow-root:last-child .MuiTableCell-root": {
                            pb: 1,
                          },
                        }}
                        className="two-table"
                      >
                        <Table aria-label="simple table">
                          <TableBody>
                            <TableRow>
                              <TableCell className="label-data">
                                {t("Order.METHOD")}
                              </TableCell>
                              <TableCell align="right">
                                {state.orderDet.orderPayment ===
                                  dataConstant.Undefined ||
                                state.orderDet.orderPayment.length === 0
                                  ? "-"
                                  : map(state.orderDet.orderPayment, (obj) => (
                                      <Typography key={obj.PaymentMethodId}>
                                        {obj.PaymentMethodId}
                                      </Typography>
                                    ))}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </DataCard>
                  </Grid>

                  {/* Items */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <DataCard>
                      <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                          background: "transparent",
                          width: "100%",
                          "& .MuiTableRow-root:last-child .MuiTableCell-root": {
                            pb: 1,
                          },
                        }}
                        className="two-table"
                      >
                        <Table aria-label="simple table">
                          <TableBody>
                            <TableRow>
                              <TableCell className="label-data">
                                {t("Order.ITEM")}
                              </TableCell>
                              <TableCell align="right">
                                {state.orderDet.orderItem.length !== 0
                                  ? state.orderDet.orderItem.map(
                                      (obj, index) => (
                                        <Box
                                          key={index}
                                          display="flex"
                                          alignItems="center"
                                          justifyContent={{
                                            xs: "flex-start",
                                            sm: "flex-end",
                                          }}
                                        >
                                          {obj.ItemName}
                                          {obj.IsFreeItem && (
                                            <Badge
                                              color="success"
                                              className="badge-glow cm-free-product"
                                              sx={{ ml: 1 }}
                                            >
                                              <Box
                                                display="flex"
                                                alignItems="center"
                                              >
                                                <Typography
                                                  variant="caption"
                                                  sx={{ ml: 0.5 }}
                                                >
                                                  Free
                                                </Typography>
                                              </Box>
                                            </Badge>
                                          )}
                                        </Box>
                                      )
                                    )
                                  : "-"}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className="label-data">
                                {t("Order.RATE")}
                              </TableCell>
                              <TableCell align="right">
                                {state.orderDet.orderItem?.length
                                  ? formatCurrency(
                                      state.orderDet.orderItem[0].UnitCost
                                    )
                                  : "-"}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className="label-data">
                                {t("Order.DISCOUNTAMOUNT")}
                              </TableCell>
                              <TableCell align="right">
                                {state.orderDet.orderItem?.length
                                  ? formatCurrency(
                                      state.orderDet.orderItem[0]
                                        .DiscountAmountPerQty
                                    )
                                  : "-"}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className="label-data">
                                {t("Order.DiscountedPrice")}
                              </TableCell>
                              <TableCell align="right">
                                {state.orderDet.orderItem?.length
                                  ? formatCurrency(
                                      state.orderDet.orderItem[0]
                                        .DiscountedPrice
                                    )
                                  : "-"}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className="label-data">
                                {t("Order.QUANTITY")}
                              </TableCell>
                              <TableCell align="right">
                                {state.orderDet.orderItem?.length
                                  ? state.orderDet.orderItem[0].Quantity
                                  : "-"}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className="label-data">
                                {t("Order.AMOUNT")}
                              </TableCell>
                              <TableCell align="right">
                                {state.orderDet.orderItem?.length
                                  ? formatCurrency(
                                      state.orderDet.orderItem[0].TotalAmount
                                    )
                                  : "-"}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </DataCard>
                  </Grid>

                  {/* Order Summary */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <DataCard>
                      <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                          background: "transparent",
                          width: "100%",
                          "& .MuiTableRow-root:last-child .MuiTableCell-root": {
                            borderBottom: "none",
                          },
                        }}
                        className="two-table"
                      >
                        <Table aria-label="simple table">
                          <TableBody>
                            <TableRow>
                              <TableCell className="label-data">
                                {t("Order.Cart Total")}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(
                                  state.orderSummryDetails.cartTotal
                                )}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className="label-data">
                                {t("Order.SUBTOTAL")}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(
                                  state.orderSummryDetails.subTotal
                                )}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className="label-data">
                                {t("Order.SubTotalDiscount")} (%)
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(
                                  state.orderSummryDetails.subTotalDiscount
                                )}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className="label-data">
                                {t("Order.SHIPPING")}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(
                                  state.orderSummryDetails.shipping
                                )}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className="label-data">
                                {t("Order.TAX")}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(state.orderSummryDetails.tax)}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell className="label-data">
                                {t("Order.OrderTotalDiscount")} (%)
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(
                                  state.orderSummryDetails.orderTotalDiscount
                                )}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={2} align="center">
                                <Stack alignItems={"center"} fontSize={"20px"}>
                                  {t("Order.TOTAL")}{" "}
                                  {formatCurrency(
                                    state.orderSummryDetails.total
                                  )}
                                </Stack>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </DataCard>
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleNavigateToOrderList}
                    sx={{ px: 3 }}
                  >
                    {t("Common.Back")}
                  </Button>
                </Box>
              </Stack>
            </Card>
          </Box>
        </Box>
      ) : null}
      <PaymentPopup
        isOpen={isPaymentPopupOpen}
        invoiceId={state.orderDet.invoiceId}
        totalAmount={state.orderSummryDetails.total}
        paymentCards={paymentCards}
        selectedCard={selectedCard}
        onCardChange={handleCardSelection}
        onClose={handlePaymentPopupClose}
        onPayment={handlePaymentSubmit}
        onNavigateToPaymentProfile={handleNavigateToPaymentProfile}
      />
      <CommonSnackBar snackObj={snack} closeSnack={closeSnack} buttonProp />
    </>
  );
};

export default ViewOrder;